import * as matchAPI from './matches'
import * as playerAPI from './player'

/*
JSON format expected:
{
  "matches": [
    {
      "date": "2024-01-15T10:30:00Z", // ISO date string
      "player1": "Player Name 1",
      "player2": "Player Name 2", 
      "outcome": "1", // "1" = player1 wins, "X" = tie, "2" = player2 wins
      "comment": "Optional comment about the match"
    }
  ]
}
*/

export const importFromJSON = (jsonData) => {
  try {
    const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData
    
    if (!data.matches || !Array.isArray(data.matches)) {
      throw new Error('Nieprawidłowy format JSON: brak lub nieprawidłowa tablica meczów')
    }

    const existingPlayers = playerAPI.getAll()
    const existingMatches = matchAPI.getAll()
    
    // Process matches chronologically by date
    const sortedMatches = data.matches
      .filter(match => match.date && match.player1 && match.player2 && typeof match.outcome !== 'undefined')
      .sort((a, b) => new Date(a.date) - new Date(b.date))

    let importedCount = 0
    let playersCreated = 0

    sortedMatches.forEach(match => {
      // Convert outcome to numeric format
      let numericOutcome
      switch (match.outcome) {
        case '1':
          numericOutcome = 1
          break
        case 'X':
          numericOutcome = 0.5
          break
        case '2':
          numericOutcome = 0
          break
        default:
          console.error('Invalid outcome:', match.outcome)
          return
      }

      // Check if match already exists (by date, players, and outcome)
      const matchExists = existingMatches.some(existingMatch => 
        existingMatch.datetime === new Date(match.date).getTime() &&
        existingMatch.player1.name === match.player1 &&
        existingMatch.player2.name === match.player2 &&
        existingMatch.outcome === numericOutcome
      )

      if (matchExists) {
        return // Skip if match already exists
      }

      // Find or create player1
      let player1 = existingPlayers.find(p => p.name === match.player1)
      if (!player1) {
        player1 = playerAPI.addNew(match.player1)
        playersCreated++
        // keep existingPlayers in sync so subsequent matches in this import see new players
        existingPlayers.push(player1)
      }

      // Find or create player2
      let player2 = existingPlayers.find(p => p.name === match.player2)
      if (!player2) {
        player2 = playerAPI.addNew(match.player2)
        playersCreated++
        existingPlayers.push(player2)
      }

      // Add the match
      const addedMatch = matchAPI.addNew(
        new Date(match.date).getTime(), // Convert to timestamp
        player1,
        player2,
        numericOutcome,
        match.comment || '' // Add comment if available
      )

      // Immediately update player ELOs using the same logic used by the app when matches are
      // added manually. Also keep existingMatches in sync to avoid duplicates within this import.
      try {
        playerAPI.updatePlayerRankings(addedMatch)
      } catch (err) {
        console.error('Failed to update player rankings for imported match:', addedMatch, err)
      }
      existingMatches.push(addedMatch)

      importedCount++
    })

    return {
      success: true,
      matchesImported: importedCount,
      playersCreated: playersCreated,
      totalMatches: sortedMatches.length
    }

  } catch (error) {
    console.error('Error importing JSON:', error)
    return {
      success: false,
      error: error.message.includes('Unexpected token') ? 'Invalid JSON format: ' + error.message : error.message
    }
  }
}

export const validateJSON = (jsonData) => {
  try {
    const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData
    
    if (!data.matches || !Array.isArray(data.matches)) {
      return { valid: false, error: 'Brak lub nieprawidłowa tablica meczów' }
    }

    const errors = []
    let validMatches = 0

    data.matches.forEach((match, index) => {
      if (!match.date) {
        errors.push(`Mecz ${index + 1}: Brak daty`)
      } else if (isNaN(new Date(match.date).getTime())) {
        errors.push(`Mecz ${index + 1}: Nieprawidłowy format daty`)
      }

      if (!match.player1) {
        errors.push(`Mecz ${index + 1}: Brak ekipy 1`)
      }

      if (!match.player2) {
        errors.push(`Mecz ${index + 1}: Brak ekipy 2`)
      }

      if (typeof match.outcome === 'undefined') {
        errors.push(`Mecz ${index + 1}: Brak wyniku`)
      } else if (!['1', 'X', '2'].includes(match.outcome)) {
        errors.push(`Mecz ${index + 1}: Nieprawidłowy wynik (musi być "1", "X" lub "2")`)
      }

      if (match.player1 && match.player2 && match.player1 === match.player2) {
        errors.push(`Mecz ${index + 1}: Ekipa 1 i Ekipa 2 nie mogą być takie same`)
      }

      if (errors.length === 0 || !errors.some(err => err.includes(`Match ${index + 1}`))) {
        validMatches++
      }
    })

    return {
      valid: errors.length === 0,
      errors,
      validMatches,
      totalMatches: data.matches.length
    }

  } catch (error) {
    return {
      valid: false,
      error: 'Nieprawidłowy format JSON: ' + error.message
    }
  }
}

export const importFromSampleFile = async () => {
  try {
    // Sample data embedded directly in the code
    const sampleData = {
      "matches": [
      {
      "date": "2024-12-01T00:00:00Z",
      "player1": "Legia Warszawa",
      "player2": "KKS Kalisz",
      "outcome": "1",
      "comment": "Legia Warszawa (13) – KKS Kalisz (13) – 12.2024. W bitwie na śnieżki stanęła ekipa z Warszawy oraz Kalisza[cite: 1]. [cite_start]Po około półtora minucie lepszą skutecznością wykazali się Legioniści[cite: 2]."
      },
      {
      "date": "2024-10-01T00:00:00Z",
      "player1": "Piast Gliwice",
      "player2": "FC Basel",
      "outcome": "2",
      "comment": "Piast Gliwice (13) – FC Basel (13) – 10.2024. [cite_start]Zawody do 23 lat[cite: 2]. [cite_start]Po około 90 sekundach lepszą sprawnością wykazali się sportowcy ze Szwajcarii[cite: 3]."
      },
      {
      "date": "2024-05-01T00:00:00Z",
      "player1": "Apator Toruń",
      "player2": "Stal Gorzów",
      "outcome": "1",
      "comment": "Apator Toruń (10) – Stal Gorzów (10) – 05.2024. [cite_start]Zawody w przeciąganiu liny z ograniczeniem wiekowym do rocznika ’05[cite: 4]. [cite_start]Po minucie i 10 sekundach zwycięstwo odnosi ekipa Apatora Toruń[cite: 5]."
      },
      {
      "date": "2024-04-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Siarka Tarnobrzeg",
      "outcome": "1",
      "comment": "Arka Gdynia (11) – Siarka Tarnobrzeg (11) – 04.2024. [cite_start]Zwycięstwo HFA (Arka Gdynia)[cite: 6]."
      },
      {
      "date": "2024-01-01T00:00:00Z",
      "player1": "Górnik Zabrze",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "2",
      "comment": "Górnik Zabrze (15) – Zagłębie Sosnowiec (15) – 01.2024. [cite_start]Torcida Górnik: “Górnik Zabrze vs. Zagłębie Sosnowiec do 26 lat, 15×15. Nasza porażka. Szacunek dla Zagłębia za honorową walkę.”[cite: 7]; [cite_start]Zagłębie Sosnowiec: “Spotkanie młodszego pokolenia po 15 osób (do 26 lat) z Górnikiem Zabrze kończy się naszym zwycięstwem. Szacunek dla Torcidy za wszystko.” [cite: 8]"
      },
      {
      "date": "2023-12-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "ROW Rybnik",
      "outcome": "1",
      "comment": "Arka Gdynia HFA (11) – ROW Rybnik (11) – 12.2023. [cite_start]Zimowa aura za oknem sprzyja bitwom na śnieżki[cite: 9]. Do tej postanowili stanąć młode składy z Gdyni i Rybnika. [cite_start]Więcej śnieżnych kul poleciało ze strony Arkowców[cite: 10]."
      },
      {
      "date": "2023-07-01T00:00:00Z",
      "player1": "ROW Rybnik",
      "player2": "KKS Kalisz",
      "outcome": "1",
      "comment": "ROW Rybnik (10) – KKS Kalisz (10) – 07.2023. [cite_start]Drużyny tego dnia liczyły po 10 osób, z ograniczeniem wiekowym do 23 lat[cite: 12]. [cite_start]Po twardej 2,20 minutowej wymianie zdań, tego dnia więcej argumentów było po stronie ROW’u Rybnik[cite: 13]."
      },
      {
      "date": "2023-06-01T00:00:00Z",
      "player1": "ŁKS Łódź",
      "player2": "Eintracht Frankfurt",
      "outcome": "1",
      "comment": "ŁKS Łódź (22) – Eintracht Frankfurt (22) – 06.2023. [cite_start]Po 50 sekundach zwycięstwo odnoszą Łodzianie[cite: 15]."
      },
      {
      "date": "2023-06-01T00:00:00Z",
      "player1": "Polonia Bytom",
      "player2": "Stilon Gorzów",
      "outcome": "1",
      "comment": "Polonia Bytom (12) – Stilon Gorzów (12) – 06.2023. [cite_start]Zawody do 26 lat[cite: 16]. [cite_start]Po 2:20 lepszą kondycją wykazali się przedstawiciele bytomskiej Polonii[cite: 17]."
      },
      {
      "date": "2023-05-01T00:00:00Z",
      "player1": "Górnik Konin",
      "player2": "Wisła Płock",
      "outcome": "1",
      "comment": "Górnik Konin (10) – Wisła Płock (10) – 05.2023. Walka do 22 lat. [cite_start]Zwycięsko z pojedynku wychodzi ekipa Górnika[cite: 18]."
      },
      {
      "date": "2023-04-01T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "Stilon Gorzów",
      "outcome": "1",
      "comment": "Pogoń Szczecin (6) – Stilon Gorzów (6) – 04.2023. [cite_start]Wygrana Portowców[cite: 18]."
      },
      {
      "date": "2023-04-01T00:00:00Z",
      "player1": "Włocłavia Włocławek",
      "player2": "Elana Toruń",
      "outcome": "2",
      "comment": "Włocłavia Włocławek (10) – Elana Toruń (10) – 04.2023. Walka do 23 lat. [cite_start]Po około 2 minutach wygrana Elany[cite: 19]."
      },
      {
      "date": "2023-03-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "JKS Jarosław",
      "outcome": "1",
      "comment": "Arka Gdynia (9) – JKS Jarosław (9) – 03.2023. Zespoły liczą po 9 osób młodego składu. [cite_start]Walkę wygrywa HFA[cite: 20]."
      },
      {
      "date": "2023-03-01T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Djurgarden (DFG)",
      "outcome": "1",
      "comment": "Lech Poznań U26 (LPH) (16) – Djurgarden (DFG) (16) – 03.2023. Walka trwa 30 sekund. [cite_start]Wygrana LPH[cite: 21]."
      },
      {
      "date": "2023-03-01T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Djurgarden (DFG)",
      "outcome": "1",
      "comment": "Lech Poznań (LPH) (21) – Djurgarden (DFG) (21) – 03.2023. Walka trwa 40 sekund. [cite_start]Wygrana LPH[cite: 22]."
      },
      {
      "date": "2022-12-01T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Raków Częstochowa",
      "outcome": "1",
      "comment": "Lech Poznań (26) – Raków Częstochowa (26) – 12.2022. [cite_start]Obie ekipy wystawiają po 26 zawodników[cite: 23]. [cite_start]Po około 4 minutowej mocnej walce lepsi okazują się Poznaniacy[cite: 24]."
      },
      {
      "date": "2022-11-01T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Banik Ostrawa",
      "outcome": "1",
      "comment": "Lech Poznań (31) – Banik Ostrawa (31) – 11.2022. [cite_start]U Banika obecna była 6-osobowa delegacja kibiców Spartaka Trnava[cite: 25]. [cite_start]Całe grzybobranie trwało minutę a zwycięsko z potyczki wyszła ekipa z Polski[cite: 26]."
      },
      {
      "date": "2022-11-01T00:00:00Z",
      "player1": "Polonia Bytom",
      "player2": "Energie Cottbus",
      "outcome": "1",
      "comment": "Polonia Bytom (12) – Energie Cottbus (Niemcy) (12) – 11.2022. [cite_start]Rywalizowano z ograniczeniem wiekowym do 25 lat[cite: 27]. [cite_start]Po 30 sekundach rywalizacji, tego dnia lepszą kondycją wykazali się Polacy[cite: 28]."
      },
      {
      "date": "2022-11-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Łada Biłgoraj",
      "outcome": "1",
      "comment": "Tczewska Arka (15) – Łada Biłgoraj (15) – 11.2022. [cite_start]Po minucie i dwudziestu sekundach zwycięsko z pojedynku wychodzi Arka[cite: 29]. [cite_start]Relacja TPAG: “Szacunek dla Łady i ich rodziny za honorowe starcia na sportowych zasadach w tym roku. SILNA ZWARTA TCZEWSKA ARKA” [cite: 30]"
      },
      {
      "date": "2022-10-01T00:00:00Z",
      "player1": "Wisłoka Dębica",
      "player2": "Chojniczanka Chojnice",
      "outcome": "1",
      "comment": "Wisłoka Dębica (20) – Chojniczanka Chojnice (20) – 10.2022. [cite_start]W zawodach w jesiennym grzybobraniu zwyciężyli ci pierwsi, odnosząc zwycięstwo po 2:27 min[cite: 30]."
      },
      {
      "date": "2022-10-01T00:00:00Z",
      "player1": "Zagłębie Sosnowiec",
      "player2": "ROW Rybnik",
      "outcome": "1",
      "comment": "Zagłębie Sosnowiec (10) – ROW Rybnik (10) – 10.2022. [cite_start]Na jesienne grzybobranie wybrała się do lasu ekipa Zagłębia oraz ROW Rybnik[cite: 31]. [cite_start]Duży wysyp grzybów w lesie przedłużył zbieranie do 80 sekund i z większym koszykiem wrócili chłopacy z Sosnowca[cite: 32]."
      },
      {
      "date": "2022-09-10T00:00:00Z",
      "player1": "KKS Kalisz",
      "player2": "Ostrovia Ostrów",
      "outcome": "1",
      "comment": "KKS Kalisz (45) – Ostrovia Ostrów (?) – 10.09.2022. [cite_start]Relacja KKS-u Kalisz: W Pruszkowie meldujemy się w 45 osób ,podróżujemy busami i autami, mamy ze sobą mała flagę Autonomia Kaliska[cite: 33]. Na powrocie naszą formę postanawia sprawdzić ekipa Ostrovii, szybkie starcie na naszą korzyść. [cite_start]Szacunek dla przeciwnika za chęci[cite: 35]."
      },
      {
      "date": "2022-09-01T00:00:00Z",
      "player1": "BKS Bielsko-Biała",
      "player2": "Chełmianka Chełm",
      "outcome": "1",
      "comment": "BKS Bielsko-Biała (15) – Chełmianka Chełm (15) – 09.2022. [cite_start]Relacja Chełmianki Chełm: Na mecz udajemy się w 41 osób[cite: 36]. Walczono open po 15 osób. Po 30 sekundach uznajemy wyższość rywali. [cite_start]Ogromny szacunek za honorową walkę oraz sposób organizacji[cite: 38]."
      },
      {
      "date": "2022-08-24T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Eintracht Frankfurt",
      "outcome": "1",
      "comment": "Lech Poznań (ok. 120) – Eintracht Frankfurt (ok. 150) – 24.08.2022. [cite_start]Pomimo przewagi liczebnej ekipy z Niemiec, lepsi okazali się kibole Lecha Poznań[cite: 39]."
      },
      {
      "date": "2022-07-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Karpaty Krosno",
      "outcome": "1",
      "comment": "Arka Gdynia (FC Tczew) (10) – Karpaty Krosno (10) – 07.2022. [cite_start]Jak informuje TPAG: Ostatnio spotykamy się z Karpaty Krosno 10×10 OPEN[cite: 40]. Po twardej walce 2min10sek nasze zwycięstwo. Szacunek za walkę. [cite_start]SPORTOWA PATOLOGIA! [cite: 41]"
      },
      {
      "date": "2022-06-01T00:00:00Z",
      "player1": "Czuwaj Przemyśl",
      "player2": "Sandecja Nowy Sącz",
      "outcome": "2",
      "comment": "Czuwaj Przemyśl (10) – Sandecja Nowy Sącz (10) – 06.2022. [cite_start]Relacja Sandecji Nowy Sącz: Ostatnim czasem spotykamy się z ekipą Czuwaju Przemyśl młodymi składami po 10 osób[cite: 42]. Po konkretnej 2-minutowej walce niestety przegrywamy.. Szacunek dla przeciwników za podejście do sprawy. [cite_start]Walczyć, trenować – Sandecja musi panować ! [cite: 43]"
      },
      {
      "date": "2022-05-01T00:00:00Z",
      "player1": "Góral Żywiec",
      "player2": "TSV 1860 Monachium",
      "outcome": "2",
      "comment": "Góral Żywiec (14) – TSV 1860 Monachium (14) – 05.2022. [cite_start]Po twardej, 2,5 min. rywalizacji lepszą kondycją wykazała się niemiecka ekipa[cite: 44]. [cite_start]Zawody odbyły się po 14 osób[cite: 45]."
      },
      {
      "date": "2022-04-01T00:00:00Z",
      "player1": "Górnik Łęczna",
      "player2": "Stal Stalowa Wola",
      "outcome": "X",
      "comment": "Górnik Łęczna (23) – Stal Stalowa Wola (24) – 04.2022. Wracając Auto SSW zaprasza do wspólnych harców w wyznaczonym miejscu. [cite_start]Korzystamy z uprzejmości[cite: 49]. Po kilkuminutowej walce awantura jest nierozstrzygnięta, remisowa, po czym SSW postanawia się zawijać. [cite_start]Zacięte starcie, bez milicji, bez poważnych kontuzji[cite: 50]. Na koniec typ ze Stalówki oznajmia, że byli sami w 24 osoby. [cite_start]Bez zastrzeżeń[cite: 51]."
      },
      {
      "date": "2022-03-01T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "Stilon Gorzów",
      "outcome": "1",
      "comment": "Pogoń Szczecin (LFS) (10) – Stilon Gorzów (10) – 03.2022. [cite_start]Wygrana Pogoni Szczecin[cite: 52]."
      },
      {
      "date": "2022-02-01T00:00:00Z",
      "player1": "Zagłębie Sosnowiec",
      "player2": "Stal Rzeszów",
      "outcome": "1",
      "comment": "Zagłębie Sosnowiec (25) – Stal Rzeszów (25) – 02.2022. [cite_start]Ostatnimi czasy zoorganizowano grillowanie dwóch mocnych ekip – Zagłębia Sosnowiec i Stali Rzeszów po 25 zawodników[cite: 54]. [cite_start]Po mocnej i honorowej walce wygrywają chuligani Zagłębia Sosnowiec[cite: 55]."
      },
      {
      "date": "2021-12-01T00:00:00Z",
      "player1": "Glinik Gorlice",
      "player2": "Igloopol Dębica",
      "outcome": "1",
      "comment": "Glinik Gorlice (5) – Igloopol Dębica (5) – 12.2021. [cite_start]Relacja MB’19: Jakiś czas temu rozgrywamy mecz po 5×5 z Igloopol Dębica do 97′[cite: 55]. Szybka nasza wygrana. Szacunek za walkę. [cite_start]Walczyć, trenować! [cite: 56]"
      },
      {
      "date": "2021-12-01T00:00:00Z",
      "player1": "Korona Kielce",
      "player2": "Wisła Płock",
      "outcome": "1",
      "comment": "Korona Kielce (8) – Wisła Płock (8) – 12.2021. [cite_start]Obie drużyny wystawiają po 8 osób[cite: 56]. Lepszą kondycją fizyczną wykazali się przedstawiciele YBH’01 (Korona Kielce). [cite_start]Jak informują sami zainteresowani, wszystko przebiegło honorowo i z wzajemnym szacunkiem[cite: 57]."
      },
      {
      "date": "2021-11-01T00:00:00Z",
      "player1": "Legia Warszawa",
      "player2": "Falubaz Zielona Góra",
      "outcome": "1",
      "comment": "Legia Warszawa (10) – Falubaz Zielona Góra (10) – 11.2021. [cite_start]Legia Warszawa (Warsaw Fans Hooligans) i Falubazu Zielona Góra (FhZG’06) sprawdziły swoją kondycję[cite: 58]. [cite_start]Obie drużyny wystawiły 10-osobowe składy do lat 25. Po minucie lepszą sprawnością fizyczną wykazali się Legioniści[cite: 59]."
      },
      {
      "date": "2021-11-01T00:00:00Z",
      "player1": "Zagłębie Sosnowiec",
      "player2": "Stal Stalowa Wola",
      "outcome": "1",
      "comment": "Zagłębie Sosnowiec (21) – Stal Stalowa Wola (21) – 11.2021. Relacja Zagłębia Sosnowiec: Zagłębie Sosnowiec vs Stal Stalowa Wola. [cite_start]21vs21[cite: 60]. Nasze zwycięstwo (ok. 60 sek). [cite_start]Szacunek dla rywali za podejście i podjęcie walki[cite: 61]."
      },
      {
      "date": "2021-09-01T00:00:00Z",
      "player1": "Kotwica Kołobrzeg",
      "player2": "Olimpia Grudziądz",
      "outcome": "1",
      "comment": "Kotwica Kołobrzeg (7) – Olimpia Grudziądz (7) – 09.2021. [cite_start]Na jesienne grzybobranie wybrała się ekipa z Kołobrzegu oraz Grudziądza[cite: 62]. [cite_start]Szybciej swoje koszyki zapełnili chłopaki z Kotwicy[cite: 63]."
      },
      {
      "date": "2021-09-22T00:00:00Z",
      "player1": "Włocłavia Włocławek",
      "player2": "Piast Gliwice",
      "outcome": "1",
      "comment": "Włocłavia Włocławek (7) – Piast Gliwice (7) – 22.09.2021. [cite_start]Na meczu wyczajamy grupkę kibiców Piasta, która później wystawia 7 osobowy skład do młodzieżowej rywalizacji w innej dyscyplinie z której wychodzimy zwycięsko[cite: 70]. [cite_start]Szacunek dla wyjazdowiczów za skorzystanie z propozycji[cite: 71]."
      },
      {
      "date": "2021-09-01T00:00:00Z",
      "player1": "GKS Katowice",
      "player2": "Sandecja Nowy Sącz",
      "outcome": "1",
      "comment": "GKS Katowice (10) – Sandecja Nowy Sącz (10) – 09.2021. [cite_start]Relacja Młode Katowice: Ostatnimi czasy kolejny raz spotykamy się na ubitej ziemi, tym razem była to ekipa z Nowego Sącza[cite: 71]. Obie ekipy wystawiły po 10 osób młodego składu (u18), a wszystko odbyło się honorowo. [cite_start]Zabawa trwała nieco ponad półtorej minuty[cite: 72]. Wygrana GieKSy. [cite_start]Dzięki za walkę! [cite: 73]"
      },
      {
      "date": "2021-08-01T00:00:00Z",
      "player1": "Beskid Andrychów",
      "player2": "Lokomotive Lipsk",
      "outcome": "1",
      "comment": "Beskid Andrychów (10) – Lokomotive Lipsk (10) – 08.2021. [cite_start]Obie drużyny wystawiły 10-osobowe składy z ograniczeniem wiekowym do 23 lat[cite: 73]. [cite_start]Tego dnia, po zaciętej rywalizacji, która trwała 2,5 minuty lepszą kondycją fizyczną wykazali się Polacy[cite: 74]."
      },
      {
      "date": "2021-08-10T00:00:00Z",
      "player1": "GKM Grudziądz",
      "player2": "Widzew Łódź",
      "outcome": "X",
      "comment": "GKM Grudziądz (14) – FC Widzew Łódź (14) – 10.08.2021. [cite_start]W Grudziądzu na jednym z osiedli spotkały się ekipy GKM-u i FC Widzewa Łódź[cite: 75]. Walka trwa około 30 sekund. [cite_start]Starcie nierozstrzygnięte, obie ekipy zdążyły się ewakuować przed przyjazdem smutnych panów[cite: 76]."
      },
      {
      "date": "2021-06-01T00:00:00Z",
      "player1": "Chełmianka Chełm",
      "player2": "Avia Świdnik",
      "outcome": "1",
      "comment": "Chełmianka Chełm (8) – Avia Świdnik (8) – 06.2021. [cite_start]Zawody w przeciąganiu liny liczyły po 8 osób z ograniczeniem wiekowym do 23 roku życia[cite: 78]. [cite_start]Tego dnia, po twardej, 3-minutowej rywalizacji lepszą kondycją wykazała się chełmska drużyna[cite: 79]."
      },
      {
      "date": "2021-01-16T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "Falubaz Zielona Góra",
      "outcome": "1",
      "comment": "Pogoń Szczecin (10) – Falubaz Zielona Góra (10) – 16.01.2021. [cite_start]Obie ekipy wystawiają 10-cio osobowe składy do 25 lat[cite: 81]. [cite_start]Po 50 sekundach lepszą wytrzymałością wykazali się przedstawiciele szczecińskiej Pogoni[cite: 82]."
      },
      {
      "date": "2020-12-05T00:00:00Z",
      "player1": "Wisłoka Dębica",
      "player2": "Stal Mielec",
      "outcome": "1",
      "comment": "Wisłoka Dębica (36) – Stal Mielec (50) – 05.12.2020. [cite_start]Relacja Wisłoki Dębica: Dzisiejszego wieczoru dostajemy info, że na naszym FC pojawia się 50-osobowa grupa mieleckiej Stali i zamalowuje grafy[cite: 83]. [cite_start]Zbieramy się na szybko w 36 osób, jedziemy w miejsce zdarzenia, gdzie widziani byli Stalowcy[cite: 84]. [cite_start]Dochodzi do dwóch starć które trwały około 2.30 min, wszystko honorowo, Nasze pewne zwycięstwo! [cite: 86]"
      },
      {
      "date": "2020-11-01T00:00:00Z",
      "player1": "Drwęca Nowe Miasto",
      "player2": "MKS Ciechanów",
      "outcome": "1",
      "comment": "Drwęca Nowe Miasto (6) – MKS Ciechanów (6) – 11.2020. [cite_start]Spotkanie do 23 lat[cite: 87]. Cała walka odbywa się honorowo i trwa dwie i pół minuty. [cite_start]Lepsi okazują się chłopaki z Nowego Miasta[cite: 88]."
      },
      {
      "date": "2020-11-01T00:00:00Z",
      "player1": "GKS Katowice",
      "player2": "Bohemias Praga",
      "outcome": "1",
      "comment": "GKS Katowice (6) – Bohemias Praga (6) – 11.2020. [cite_start]Ekipy wystawiają młode składy po 6 osób do 18 lat[cite: 89]. [cite_start]Zwycięstwo po 20 sekundach GKSu Katowice[cite: 90]."
      },
      {
      "date": "2020-11-01T00:00:00Z",
      "player1": "GKS Katowice",
      "player2": "Pogoń Szczecin",
      "outcome": "1",
      "comment": "GKS Katowice (11) – Pogoń Szczecin (11) – 11.2020. [cite_start]Młode składy do lat 19. Walka trwa aż 2 i pół minuty[cite: 90]. [cite_start]Lepsi okazują się chłopaki z Katowic[cite: 91]."
      },
      {
      "date": "2020-10-17T00:00:00Z",
      "player1": "Czuwaj Przemyśl",
      "player2": "Arka Gdynia",
      "outcome": "2",
      "comment": "Czuwaj Przemyśl (10) – Arka Gdynia (10) – 17.10.2020. [cite_start]Ekipy wystawiają młode składy po 10 osób[cite: 91]. [cite_start]Lepsza okazuję się Arka[cite: 92]."
      },
      {
      "date": "2020-10-11T00:00:00Z",
      "player1": "Sparta Szepietowo",
      "player2": "Warmia Grajewo",
      "outcome": "2",
      "comment": "Sparta Szepietowo – Warmia Grajewo – 11.10.2020. Fanatycy Warmii: Spotkanie z ekipą ziomków naszych boiskowych rywali. [cite_start]Kolejny fight w regionie i znów górą WGHME ! [cite: 93] Szacunek tym przeciwnikom, którzy podjęli walkę. [cite_start]WARMIA&MAZUR !!! [cite: 94]"
      },
      {
      "date": "2020-09-27T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "2",
      "comment": "Lech Poznań (33) – Zagłębie Sosnowiec (33) – 27.09.2020. [cite_start]Solidne składy Lecha Poznań i Zagłębia Sosnowiec postanowiły dziś sprawdzić swoje siły podczas grzybobrania[cite: 94]. [cite_start]Obie ekipy wystawiają po 33 grzybiarzy, którzy udają się do lasu w celu zapełniania swoich koszyków[cite: 95]. [cite_start]Szybciej swoje koszyki zapełnili chłopaki z Zagłębia[cite: 97]."
      },
      {
      "date": "2020-08-29T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "Eintracht Frankfurt",
      "outcome": "1",
      "comment": "Pogoń Szczecin (25) – Eintracht Frankfurt (25) – 29.08.2020. [cite_start]W zawodach bierze udział po 25 osób[cite: 97]. [cite_start]Po około 2 minutach lepsi okazują się kibole Pogoni[cite: 98]."
      },
      {
      "date": "2020-07-18T00:00:00Z",
      "player1": "Slovan Bratysława",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "2",
      "comment": "Slovana (USP) (15) – Zagłębie Sosnowiec (BB) (15) – 18.07.2020. [cite_start]Ultras Slovan official: Dziś odbyło się międzynarodowe spotkanie Slovana (USP) i Zagłębie Sosnowiec (BB) w okresie od 15×15 do 23 lat (w tym od 3 do 30 lat)[cite: 101]. [cite_start]Po 45 sekundach polska drużyna dochodzi do zwycięstwa[cite: 102]."
      },
      {
      "date": "2020-06-20T00:00:00Z",
      "player1": "Raków Częstochowa",
      "player2": "ŁKS Łódź",
      "outcome": "1",
      "comment": "Raków Częstochowa (10) – ŁKS Łódź (10) – 20.06.2020. [cite_start]Smakosze z każdej ekipy wystawili po 10 zawodników do 1997 rocznika[cite: 104]. Uczta trwała na tyle długo, że przeciągneła się do 3 minut. [cite_start]Więcej kiełbasek zjadają chłopaki z Częstochowy[cite: 105]."
      },
      {
      "date": "2020-05-30T00:00:00Z",
      "player1": "Widzew Łódź",
      "player2": "GKS Bełchatów",
      "outcome": "1",
      "comment": "Widzew Łódź – Fan Club Bełchatów – GKS Bełchatów – 30.05.2020. [cite_start]Widzew Łódź – Fan Club Bełchatów: W sobotę w Bełchatowie w godzinach okołopołudniowych spotykamy się z ogórkami banda na bandę, po około 3 minutach nasze zwycięstwo[cite: 106]."
      },
      {
      "date": "2020-05-18T00:00:00Z",
      "player1": "Ruch Chorzów",
      "player2": "Odra Wodzisław",
      "outcome": "1",
      "comment": "Ruch Chorzów – Odra Wodzisław – 18.05.2020. [cite_start]“Ultras Niebiescy”: Dziś korzystamy z zaproszenia Odry Wodzisław do sprawdzenia formy po kwarantannie na ubitej glebie[cite: 108]. [cite_start]Po półtorej minuty walki banda na bandę zdecydowane zwycięstwo Niebieskiego Południa[cite: 109]."
      },
      {
      "date": "2020-02-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Orlęta Radzyń",
      "outcome": "1",
      "comment": "Tczewska Arka (11) – Orlęta Radzyń (11) – 02.2020. [cite_start]TPAG: Jakiś czas temu spotykamy się z Orlęta Radzyń Podlaski po 11 osób do 25 lat[cite: 110]. Po około minucie nasze zwycięstwo! [cite_start]Szacunek dla przeciwnika i dzięki za walkę[cite: 111]."
      },
      {
      "date": "2019-12-01T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Odra Wodzisław",
      "outcome": "1",
      "comment": "Tczewska Arka (10) – Odra Wodzisław (10) – 12.2019. [cite_start]TPAG: Na zakończenie zeszłego roku, spotykamy się z Odrą Wodzisław po 10 (Open)[cite: 112]. Po 40 sekundach nasze zwycięstwo. Szacunek dla przeciwnika i dzięki za walkę. [cite_start]SILNA ZWARTA TCZEWSKA ARKA! [cite: 113]"
      },
      {
      "date": "2019-11-01T00:00:00Z",
      "player1": "Wisłoka Dębica",
      "player2": "Beskid Andrychów",
      "outcome": "1",
      "comment": "Wisłoka Dębica (10) – Beskid Andrychów (10) – listopad 2019. Relacja Wisłoka Dębica: W ostatnim czasie spotykamy się młodymi składami z Beskidem Andrychów po 10 osób[cite: 116]. Po 1:30 sekundach, nasze zwycięstwo. [cite_start]Wszystko honorowo i jak należy! [cite: 117]"
      },
      {
      "date": "2019-11-16T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "FC Basel",
      "outcome": "1",
      "comment": "Pogoń Szczecin (18) – FC Basel (18) – 16.11.2019. [cite_start]Doszło do zawodów przeciągania liny, między Polską ekipa – Pogoń Szczecin a szwajcarskim FC Basel[cite: 118]. [cite_start]Drużyny tego dnia liczyły po 18 osób, po minucie i 45 sekundach zwycięstwo odniosła ekipa z Polski[cite: 119]."
      },
      {
      "date": "2019-09-22T00:00:00Z",
      "player1": "Legia Chełmża",
      "player2": "Kotwica Kołobrzeg",
      "outcome": "2",
      "comment": "Legia Chełmża (10) – Kotwica Kołobrzeg (10) – 22.09.2019. [cite_start]Na grzybobraniu w lesie spotkało się dwudziestu osiłków[cite: 128]. [cite_start]Po trzech minutach i 30 sekundach więcej grzybów zebrali osoby z Kołobrzegu[cite: 129]."
      },
      {
      "date": "2019-09-08T00:00:00Z",
      "player1": "Korona Kielce",
      "player2": "Banik Ostrava",
      "outcome": "1",
      "comment": "Young Boys Hooligans ’01 (Korona Kielce) (20) – Chuligani z Bazalu (Banik Ostrava) (20) – 08.09.2019. [cite_start]Relacja YBH’01: Wykorzystując przerwe reprezentacyjną na zaproszenie Banika Ostrawa, podejmujemy rywalizacje[cite: 130]. Mecz rozegrany w składach 20 osobowych. [cite_start]Po 1.30 nasze zwycięstwo[cite: 131]."
      },
      {
      "date": "2019-06-12T00:00:00Z",
      "player1": "Stal Rzeszów",
      "player2": "Czuwaj Przemyśl",
      "outcome": "1",
      "comment": "Stal Rzeszów (16) – Czuwaj Przemyśl (16) – 12.06.2019. [cite_start]Po ponad 3 minutowym grillowaniu więcej kiełbasek zjadają smakosze Stali[cite: 135]."
      },
      {
      "date": "2019-06-02T00:00:00Z",
      "player1": "AZS Podlasie",
      "player2": "Wisła Sandomierz",
      "outcome": "1",
      "comment": "AZS Podlasie (10) – Wisła Sandomierz (10) – 02.06.2019. [cite_start]Naprzeciwko siebie stanęli reprezentanci AZS-u Podlasie i Wisły Sandomierz, obie drużyny składały się po 10 osób[cite: 137]. [cite_start]Tego dnia po minucie i 30 sekundach lepszą kondycją wykazali się przedstawiciele z Lubelszczyzny[cite: 138]."
      },
      {
      "date": "2019-05-27T00:00:00Z",
      "player1": "Mazur Ełk",
      "player2": "Chełmianka Chełm",
      "outcome": "1",
      "comment": "Mazur Ełk (9) – Chełmianka Chełm (9) – 27.05.2019. [cite_start]Walka odbyła się po 9 osób z ograniczeniem do 96 rocznika[cite: 140]. [cite_start]Po około 1.5 minucie lepszą kondycją wykazali się przedstawiciele Mazura[cite: 141]."
      },
      {
      "date": "2019-04-27T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "GKS Katowice",
      "outcome": "1",
      "comment": "Lech Poznań (30) – GKS Katowice (30) – 27.04.2019. [cite_start]Dziś dochodzi do przeciągania liny między ekipą Lecha Poznań (Young Freaks’98) vs. GKS Katowice (Persona non Grata)[cite: 142]. Zawody odbywają się po 30 zawodników. [cite_start]Po ciężkiej walce, która trwała 4 minuty wygrywa Lech[cite: 143]."
      },
      {
      "date": "2019-04-01T00:00:00Z",
      "player1": "AZS Podlasie",
      "player2": "KSZO Ostrowiec",
      "outcome": "1",
      "comment": "AZS Biała Podlaska (10) – KSZO Ostrowiec (10) – kwiecień 2019. Jak informują sami zainteresowani (AZS Podlasie): Przeciwnikiem był KSZO Ostrowiec Świętokrzyski. Walka odbyła się po 10 w kategorii wiekowej do 26 lat. [cite_start]Po 1.15 nasze zwycięstwo[cite: 150]."
      },
      {
      "date": "2019-04-07T00:00:00Z",
      "player1": "GKS Jastrzębie",
      "player2": "Odra Opole",
      "outcome": "2",
      "comment": "GKS Jastrzębie (30) – Odra Opole (30) – 07.04.2019. [cite_start]Wspólne grillowanie GKSu Jastrzębie i Odry Opole po 30 smakoszy[cite: 154]. [cite_start]Po półtorej minucie więcej kiełbasek zjada ekipa Odry Opole[cite: 155]."
      },
      {
      "date": "2019-03-24T00:00:00Z",
      "player1": "Lechia Gdańsk",
      "player2": "Spartak Moskwa",
      "outcome": "1",
      "comment": "Lechia Gdańsk (30) – Spartak Moskwa (30) – 24.03.2019. [cite_start]Kibice Lechii Gdańsk w słoneczne marcowe popołudnie zaprosili do wspólnego grillowania ekipę Spartaka Moskwa (z grupy Boxers Team), którzy wystawili po 30 smakoszy[cite: 155]. [cite_start]Po przeszło 2 i pół minutowej imprezie więcej kiełbasek zjada ekipa z Polski ! [cite: 156]"
      },
      {
      "date": "2019-03-01T00:00:00Z",
      "player1": "Chełmianka Chełm",
      "player2": "Stal Sanok",
      "outcome": "1",
      "comment": "Chełmianka Chełm (11) – Stal Sanok (11) – marzec 2019. Naprzeciw siebie stanęły 11-osobowe zespoły Chełmianki Chełm i Stali Sanok[cite: 157]. [cite_start]Zwycięstwo odnieśli reprezentanci z Lubelszczyzny[cite: 158]."
      },
      {
      "date": "2019-02-16T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Pogoń Szczecin",
      "outcome": "1",
      "comment": "Lech Poznań (15) – Pogoń Szczecin (15) – 16.02.2019. [cite_start]Młode składy Lecha Poznań i Pogoni Szczecin postanowili sprawdzić swoje siły w przeciąganiu liny[cite: 160]. [cite_start]Szybciej ( bo zaledwie w pół minuty ) przeciągnęli linę na swoją stronę sportowcy z Poznania[cite: 161]."
      },
      {
      "date": "2018-10-24T00:00:00Z",
      "player1": "Korona Kielce",
      "player2": "Stomil Olsztyn",
      "outcome": "1",
      "comment": "Korona Kielce (3) – Stomil Olsztyn (3) – 24.10.2018. [cite_start]Na powrocie kibice Korony Kielce postanowili zaprosić Stomilowców na szybki sparing, trzy osoby skorzystały z zaproszenia[cite: 163]. [cite_start]Tego dnia w lepszej formie byli Koroniarze[cite: 164]."
      },
      {
      "date": "2018-10-14T00:00:00Z",
      "player1": "Gwardia Koszalin",
      "player2": "ŁKS Łomża",
      "outcome": "2",
      "comment": "Gwardia Koszalin (9) – ŁKS Łomża (9) – 14.10.2018. [cite_start]Każda z drużyn wystawiła 9-osobowe zespoły, wszyscy grzybiarze nie mieli więcej niż 24 lata[cite: 165]. [cite_start]Po około 1,5 minuty szybciej swoje koszyki napełnili Łomżanie[cite: 166]."
      },
      {
      "date": "2018-10-06T00:00:00Z",
      "player1": "GKS Tychy",
      "player2": "Górnik Zabrze",
      "outcome": "2",
      "comment": "GKS Tychy FC Czułów – Górnik Zabrze FC Orzesze – 06.10.2018. [cite_start]Torcida Orzesze: W Sobote po zakończeniu meczu B klasy LKS Gardawice (Górnik Zabrze FC Orzesze) – Czułowianka Tychy (GKS Tychy FC Czułów) dochodzi do starcia około 12vs10[cite: 167]. [cite_start]Po honorowym starciu młoda banda z Orzesza wychodzi zwycięsko[cite: 168]."
      },
      {
      "date": "2018-09-08T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "2",
      "comment": "Arka Gdynia (20) – Zagłębie Sosnowiec (20) – 08.09.2018. Po zabraniu odpowiednich ekwipunków ruszyli do wspólnej biesiady w ponad 40 osób. [cite_start]Więcej kiełbasek zjedli kibole Zagłębia Sosnowiec[cite: 179]."
      },
      {
      "date": "2018-07-31T00:00:00Z",
      "player1": "Slovan Bratysława",
      "player2": "Legia Warszawa",
      "outcome": "2",
      "comment": "Slovana Bratysława (18) – Legia Warszawa (18) – 31.07.2018. Obie ekipy wystawiły wyselekcjonowany skład po 18 osób. [cite_start]Szybciej swój koszyk napełnili Polacy[cite: 181]."
      },
      {
      "date": "2018-07-14T00:00:00Z",
      "player1": "Unia Tarnów",
      "player2": "KSZO Ostrowiec Świętokrzyski",
      "outcome": "1",
      "comment": "Unia Tarnów (15) – KSZO Ostrowiec Świętokrzyski (15) – 14.07.2018. [cite_start]Po około minucie i 45 sekundach więcej kiełbasek zjadają chłopacy z Unii Tarnów[cite: 183]."
      },
      {
      "date": "2018-06-06T00:00:00Z",
      "player1": "Karpaty Krosno",
      "player2": "AZS Podlasie",
      "outcome": "1",
      "comment": "Karpaty Krosno (9) – AZS Biała-Podlaska (9) – 06.06.2018. [cite_start]AZS Podlasie: Nas w Krośnie 10 osób[cite: 184]. Proponujemy Karpatom zawody w przeciąganiu liny. Tym razem górą był przeciwnik. [cite_start]Mocno Biała! [cite: 185]"
      },
      {
      "date": "2018-04-14T00:00:00Z",
      "player1": "AZS Podlasie",
      "player2": "Mazur Ełk",
      "outcome": "1",
      "comment": "AZS Biała-Podlaska (10) – Mazur Ełk (10) – 14.04.2018. [cite_start]Obie drużyny składały się z 10 osób z ograniczeniem wiekowym do 24 lat[cite: 189]. [cite_start]Po półtorej minuty rywalizacji zwycięstwo notuje ekipa AZS-u[cite: 190]."
      },
      {
      "date": "2018-03-11T00:00:00Z",
      "player1": "Orlęta Radzyń",
      "player2": "Unia Tarnów",
      "outcome": "1",
      "comment": "Orlęta Radzyń (10) – Unia Tarnów (10) – 11.03.2018. [cite_start]Zwycięsko z tego spotkania wychodzą młodzi reprezentanci Orląt Radzyń[cite: 191]."
      },
      {
      "date": "2017-11-18T00:00:00Z",
      "player1": "GKM Grudziądz",
      "player2": "Olimpia Grudziądz",
      "outcome": "2",
      "comment": "GKM Grudziądz (10) – Olimpia Grudziądz (10) – 18.11.2017. GKM oraz Olimpia wystawiły 10 osobowe zespoły młodych grzybiarzy. [cite_start]Swoje koszyki po 1,5 minuty napełniła Olimpia[cite: 197]."
      },
      {
      "date": "2017-10-29T00:00:00Z",
      "player1": "Korona Kielce",
      "player2": "Avia Świdnik",
      "outcome": "1",
      "comment": "Korona Kielce (12) – Avia Świdnik (12) – 29.10.2017. [cite_start]Po wyselekcjonowaniu najlepszych 12 grzybiarzy więcej leśnego przysmaku udaje się zebrać młodzieży z Kielc[cite: 199]."
      },
      {
      "date": "2017-10-07T00:00:00Z",
      "player1": "Unia Leszno",
      "player2": "Lech Poznań",
      "outcome": "2",
      "comment": "Unia Leszno “Łobuzerka17” (10) – Lech Poznań “Fc Leszno” (10) – 07.10.2017. [cite_start]Relacja FC Leszno: Wyjaśniono kilkuletni spór o miasto w starciu 10×10 do rocznika 95 po 40 sekundach rozstrzygnięto na korzyść Fc Leszno którzy okazali się lepsi w zbiorach[cite: 206]."
      },
      {
      "date": "2017-09-30T00:00:00Z",
      "player1": "Widzew Łódź",
      "player2": "Resovia Rzeszów",
      "outcome": "1",
      "comment": "Widzew Łódź (20) – Resovia Rzeszów (20) – 30.09.2017. [cite_start]Obie ekipy zabrały ze sobą po 20 koszyków i w słoneczne popołudnie ruszyło głęboko w las[cite: 210]. [cite_start]Łódzcy smakosze grzybów potrzebowali zaledwie 45 sekund, aby zapełnić swoje koszyki w całości[cite: 211]."
      },
      {
      "date": "2017-09-16T00:00:00Z",
      "player1": "GKS Katowice",
      "player2": "Gwardia Koszalin",
      "outcome": "1",
      "comment": "GKS Katowice (7) – Gwardia Koszalin (7) – 16.09.2017. [cite_start]Po 15 sekundach nasza siódemka wygrała to spotkanie[cite: 213]."
      },
      {
      "date": "2017-09-09T00:00:00Z",
      "player1": "Odra Wodzisław Śląski",
      "player2": "Start Namysłów",
      "outcome": "1",
      "comment": "Odra Wodzisław Śląski (10) – Start Namysłów (10) – 09.09.2017. Podczas grzybobrania spotykają się po 10 osób. [cite_start]Większy koszyk z grzybami mieli kibole Odry[cite: 215]."
      },
      {
      "date": "2017-09-02T00:00:00Z",
      "player1": "GKS Jastrzębie",
      "player2": "GKS Tychy",
      "outcome": "2",
      "comment": "GKS Jastrzębie (70) – GKS Tychy (70) – 02.09.2017. Zaproponowano grzybobranie w liczbach ok. 70×70 z lekką przewagą Tyskich. [cite_start]Grzybobranie trwało długo, bo około 5-6 minut[cite: 217]. [cite_start]Rezultatem pierwszej rundy grzybobrania był remis, w drugiej kibice GKS-u Tychy z przewagą[cite: 218]."
      },
      {
      "date": "2017-08-05T00:00:00Z",
      "player1": "Sokół Nisko",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "1",
      "comment": "Sokół Nisko (5) – Zagłębie Sosnowiec (5) – 05.08.2017. [cite_start]Tego samego dnia trafiamy na autko kibiców Zagłębia Sosnowiec, którzy wracali z Łęcznej i zapraszamy ich po pięciu na wspólne grzybobranie, po minucie zbieramy więcej grzybów[cite: 225]."
      },
      {
      "date": "2017-08-04T00:00:00Z",
      "player1": "Ruch Chorzów",
      "player2": "Górnik Zabrze",
      "outcome": "1",
      "comment": "Ruch Chorzów (30) – Górnik Zabrze (30) – 04.08.2017. [cite_start]Wczoraj na Piekarach Śląskich dochodzi do starcia pomiędzy Ruchem i Górnikiem (ok po 30 os)[cite: 226]. [cite_start]Podczas gdy dochodzi do wymiany , kibice Torcidy pala wrotki i uciekają w bloki[cite: 227]."
      },
      {
      "date": "2017-06-10T00:00:00Z",
      "player1": "AZS Podlasie",
      "player2": "Avia Świdnik",
      "outcome": "1",
      "comment": "AZS Podlasie (25) – Avia Świdnik (25) – 10.06.2017. [cite_start]AZS Podlasie zjada więcej kiełbasek[cite: 229]."
      },
      {
      "date": "2017-05-15T00:00:00Z",
      "player1": "Odra Opole",
      "player2": "Chrobry Głogów",
      "outcome": "1",
      "comment": "Odra Opole (15) – Chrobry Głogów (15) – 15.05.2017. Obie ekipy wystawiły 15-osobowe drużyny. [cite_start]Odra zjada więcej kiełbasek[cite: 230]."
      },
      {
      "date": "2017-05-14T00:00:00Z",
      "player1": "Stilon Gorzów",
      "player2": "Odra Wodzisław",
      "outcome": "1",
      "comment": "Stilon (15) – Odra Wodzisław (15) – 14.05.2017. [cite_start]Do grillowania dochodzi w młodym składzie ( do rocznika 96 )[cite: 231]. Grill rozpalał się ponad 4 minuty. [cite_start]Więcej kiełbasek zjada Stilon[cite: 232]."
      },
      {
      "date": "2017-03-03T00:00:00Z",
      "player1": "Ruch Chorzów",
      "player2": "Wisłoka Dębica",
      "outcome": "1",
      "comment": "Ruch Chorzów (ok 30) – Wisłoka Dębica (ok 30) – 03.03.2017. [cite_start]Relacja PF: Wyczajamy, że Wisłoka jedzie do Zabrza[cite: 233]. [cite_start]Ustawiamy się na nich na trasie i dochodzi do starcia[cite: 234]. [cite_start]Nas ok 30 osób (w drodze była Wisła, ale nie zdążyli dojechać), Wisłoki ok 30. Nasze zwycięstwo[cite: 237]."
      },
      {
      "date": "2017-02-18T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Stilon Gorzów",
      "outcome": "1",
      "comment": "Arka z Tczewa (17) – Stilon Gorzów (17) – 18.02.2017. [cite_start]Obie grupy wystawiły po 17 zawodników z młodego składu[cite: 239]. [cite_start]Po minucie i 40 sekundach jako pierwsi grilla rozpalili kibice Arki z Tczewa[cite: 240]."
      },
      {
      "date": "2017-01-15T00:00:00Z",
      "player1": "Resovia Rzeszów",
      "player2": "Wisłoka Dębica",
      "outcome": "1",
      "comment": "Resovia Rzeszów (25) – Wisłoka Dębica (25) – 15.01.2017. [cite_start]W dniu dzisiejszym dochodzi do rywalizacji sekcji gimnastycznych Resovii i Wisłoki Dębica po 25 osób[cite: 243]. [cite_start]Po 36 sekundach lepsi okazują się Rzeszowianie[cite: 244]."
      },
      {
      "date": "2016-11-26T00:00:00Z",
      "player1": "Pogoń Szczecin",
      "player2": "Zagłębie Sosnowiec",
      "outcome": "2",
      "comment": "Pogoń Szczecin – Zagłębie Sosnowiec – 26.11.2016. [cite_start]Po wspólnej biesiadzie więcej kiełbasek zjada ekipa Zagłębia[cite: 245]."
      },
      {
      "date": "2016-10-31T00:00:00Z",
      "player1": "Radomiak Radom",
      "player2": "Raków Częstochowa",
      "outcome": "1",
      "comment": "Radomiak Radom (38) – Raków Częstochowa (38) – 31.10.2016. [cite_start]W ten weekend dochodzi do zawodów w przeciąganiu liny między Radomiakiem a Rakowem po 38 osób[cite: 247]. [cite_start]Po 1:20 minutach lepsza okazała się ekipa Radomiaka[cite: 248]."
      },
      {
      "date": "2016-10-01T00:00:00Z",
      "player1": "Stomil Olsztyn",
      "player2": "Mazur Ełk",
      "outcome": "1",
      "comment": "Stomil Olsztyn (11) – Mazur Ełk (11) – 01.10.2016. [cite_start]Spotkanie odbyło się do 21 lat[cite: 248]. [cite_start]Po dość dobrej 2 i pół minutowej walce lepsi okazali się Stomilowcy[cite: 249]."
      },
      {
      "date": "2016-08-21T00:00:00Z",
      "player1": "KKS Kalisz",
      "player2": "Zawisza Bydgoszcz",
      "outcome": "2",
      "comment": "KKS Kalisz – Zawisza Bydgoszcz – 21.08.2016. [cite_start]Na wracających z Gdyni kibiców KKS-u Kalisz czeka na trasie Zawisza Bydgoszcz[cite: 250]. Tylko jedno auto z Kalisza podjęło walkę – reszta aut cofa się. [cite_start]Starcie i cała akcja na korzyść Zawiszy! [cite: 251]"
      },
      {
      "date": "2016-08-20T00:00:00Z",
      "player1": "GKS Bełchatów",
      "player2": "Widzew Łódź",
      "outcome": "2",
      "comment": "GKS Bełchatów (3) – Widzew Łódź (3) – 20.08.2016. [cite_start]GKS Bełchatów – Widzew Łódź (FC Będków)[cite: 252]. [cite_start]Po 30 sekundach pierwszego grzyba znaleźli kibice RTS-u[cite: 253]."
      },
      {
      "date": "2016-08-19T00:00:00Z",
      "player1": "Petrochemia Płock",
      "player2": "Widzew Łódź",
      "outcome": "2",
      "comment": "Petrochemia Płock (30) – Widzew Łódź (30) – 19.08.2016. [cite_start]Do sprawdzenia sił doszło na autostradzie w wyrównanej sile około 30 osób ( reszta osób z jednej jak i drugiej strony rozbita po trasie )[cite: 254]. Z Widzewem jedno auto Elany Toruń. [cite_start]Po około 3 minutach honorowej i wyrównanej walki zwycięsko wychodzi Widzew[cite: 255]."
      },
      {
      "date": "2016-08-06T00:00:00Z",
      "player1": "Hetman Zamość",
      "player2": "Karpaty Krosno",
      "outcome": "1",
      "comment": "Hetman Zamość (10) – Karpaty Krosno (10) – 06.08.2016. [cite_start]Grzybobranie po 10 zawodników do 22 lat[cite: 256]. [cite_start]Po 1 minucie i 40 sekundach swój koszyk jako pierwsi napełnili kibice Hetmana Zamość[cite: 257]."
      },
      {
      "date": "2016-06-18T00:00:00Z",
      "player1": "Stal Rzeszów",
      "player2": "Zagłębie Lubin",
      "outcome": "2",
      "comment": "Stali Rzeszów (10) – Zagłębie Lubin (10) – 18.06.2016. [cite_start]Kibice z Dolnego Śląska po około 3 minutach jako pierwsi napełnili swoje koszyki[cite: 258]."
      },
      {
      "date": "2016-04-23T00:00:00Z",
      "player1": "Spartak Moskwa",
      "player2": "Legia Warszawa",
      "outcome": "1",
      "comment": "Spartak Moskwa (63) – Legia Warszawa (63) – 23.04.2016. [cite_start]Polskę reprezentowali kibice Legii Warszawa w liczbie 63 osób, Rosję natomiast reprezentowali kibice Spartaka Moskwa również w liczbie 63 osób[cite: 260]. [cite_start]Po 90 sekundach przeciągania liny silniejsi okazali się Rosjanie[cite: 262]."
      },
      {
      "date": "2016-04-23T00:00:00Z",
      "player1": "KKS Kalisz",
      "player2": "Lech Poznań",
      "outcome": "2",
      "comment": "KKS Kalisz (8) – Lech Poznań (8) – 23.04.2016. W drodze powrotnej odbyły się zawody w rozpalaniu grilla z kibicami Lecha Poznań. [cite_start]Składy 8×8, wygrana kibiców Kolejorza[cite: 263]."
      },
      {
      "date": "2016-04-16T00:00:00Z",
      "player1": "Zagłębie Sosnowiec",
      "player2": "Polonia Bytom",
      "outcome": "1",
      "comment": "Zagłębie Sosnowiec (15) – Polonia Bytom (15) – 16.04.2016. [cite_start]Młode składy, po 15 osób, zwycięstwo Zagłębia[cite: 264]."
      },
      {
      "date": "2015-09-12T00:00:00Z",
      "player1": "Legia Warszawa",
      "player2": "Elana Toruń",
      "outcome": "1",
      "comment": "Legia Warszawa (25) – Elana Toruń (25) – 12.09.2015. [cite_start]Wspólne grillowanie odbyło się po 25 osób młodego składu[cite: 268]. [cite_start]Po około 2 minutach wygrywają Legioniści[cite: 269]."
      },
      {
      "date": "2015-08-02T00:00:00Z",
      "player1": "Arka Gdynia",
      "player2": "Olimpia Grudziądz",
      "outcome": "1",
      "comment": "Arka FC TCZEW (10) – Olimpia Grudziądz (10) – 02.08.2015. [cite_start]W ubiegłą niedzielę odbyło się grzybobranie Arka FC TCZEW vs Olimpia Grudziądz po 11 osób młodymi składami w którym po minucie zwycięża Tczewska ARKA[cite: 269]."
      },
      {
      "date": "2015-06-28T00:00:00Z",
      "player1": "Elana Toruń",
      "player2": "GKS Jastrzębie",
      "outcome": "1",
      "comment": "Elana Toruń (30) – GKS Jastrzębie (30) – 28.06.2015. [cite_start]Po 80-90 sekundach jako pierwsi ognisko rozpalili fani z Torunia[cite: 271]."
      },
      {
      "date": "2015-05-09T00:00:00Z",
      "player1": "Stal Stalowa Wola",
      "player2": "Lech Poznań",
      "outcome": "2",
      "comment": "Stali Stalowa Wola (50) – Lech Poznań (50) – 09.05.2015. [cite_start]W drużynach 50-osobowych szybsi w rozpaleniu ogania okazali się Poznaniacy, którzy po minucie odnieśli zwycięstwo[cite: 272]."
      },
      {
      "date": "2015-04-26T00:00:00Z",
      "player1": "Resovia Rzeszów",
      "player2": "Czuwaj Przemyśl",
      "outcome": "1",
      "comment": "Resovia Rzeszów (15) – Czuwaj Przemyśl (15) – 26.04.2015. [cite_start]Do spotkania dochodzi młodymi składami Resovii Rzeszów i Czuwaju Przemyśl[cite: 273]. [cite_start]Po minucie więcej kiełbasek upiekli kibice z Rzeszowa[cite: 274]."
      },
      {
      "date": "2015-04-24T00:00:00Z",
      "player1": "GKS Katowice",
      "player2": "Korona Kielce",
      "outcome": "1",
      "comment": "GKS Katowice (18) – Korona Kielce (18) – 24.04.2015. [cite_start]Po minucie lepsza okazała się GieKSa[cite: 274]."
      },
      {
      "date": "2015-04-10T00:00:00Z",
      "player1": "Górnik Wałbrzych",
      "player2": "Stal Stalowa Wola",
      "outcome": "1",
      "comment": "Górnik Wałbrzych (4) – Stal Stalowa Wola (4) – 10.04.2015. Zaproponowali małe spotkanie 4×4 na co kibice Stali przystali. [cite_start]Mimo przewagi liczebnej ze strony Górnika wszystko odbyło się honorowo, wygrana gospodarzy[cite: 277]."
      },
      {
      "date": "2015-04-04T00:00:00Z",
      "player1": "Stal Poniatowa",
      "player2": "Wisła Puławy",
      "outcome": "1",
      "comment": "Stal Poniatowa (9) – Wisła Puławy (9) – 04.04.2015. [cite_start]Starcie 9 na 9 do 23 lat po ok minucie i 30 sekundach wygrywa ekipa Stali[cite: 278]."
      },
      {
      "date": "2015-03-01T00:00:00Z",
      "player1": "Karpaty Krosno",
      "player2": "Stal Sanok",
      "outcome": "2",
      "comment": "FC Rymanów (Karpaty Krosno) 16 – 15 FC Besko (Stal Sanok) – 01.03.2015. [cite_start]Po krótkim czasie wygrywa Stal Sanok[cite: 279]."
      },
      {
      "date": "2014-11-30T00:00:00Z",
      "player1": "Lech Poznań",
      "player2": "Lechia Gdańsk",
      "outcome": "1",
      "comment": "Lech (73) – Lechia (73) – 30.11.2014. [cite_start]Po 2 minutach wygrywa Lech[cite: 282]."
      },
      {
      "date": "2014-11-07T00:00:00Z",
      "player1": "Górnik Zabrze",
      "player2": "Górnik Łęczna",
      "outcome": "1",
      "comment": "Górnik Zabrze (12) – Górnik Łęczna (12) – 07.11.2014. [cite_start]Walka po 12 osób z ograniczeniem wiekowym do rocznika 1988[cite: 284]. Po ok. [cite_start]1,5 minucie walki zwycięża TORCIDA[cite: 285]."
      }
      ]
     }
    
    return importFromJSON(JSON.stringify(sampleData))
  } catch (error) {
    console.error('Error importing from sample file:', error)
    return {
      success: false,
      error: error.message
    }
  }
}
